/**
 * Site-wide / user-targeted announcements (Firestore-backed, admin-editable).
 *
 * Lives in its own top-level `announcements` collection. Targeting:
 *   - mode: 'all'      -> visible to every signed-in user
 *   - mode: 'selected' -> visible only when uid in target.uids
 *
 * Segment filters (role, plan tier, subscription status, has farms) are
 * resolved into a snapshot of `target.uids` at save time so the listing
 * queries stay simple and statically provable in firestore.rules.
 *
 * The announcement banner reuses the promo banner palette so visual
 * placement and styling stay identical when an announcement overrides
 * the promo banner.
 */

import { VARIANT_STYLES as PROMO_VARIANT_STYLES } from './promoBanner';

export const ANNOUNCEMENTS_COLLECTION = 'announcements';

/** localStorage key prefix for per-announcement dismissal (keyed by id+version). */
export const ANNOUNCEMENT_DISMISS_KEY_PREFIX = 'ecointel.announcement.dismissed.';

export const ANNOUNCEMENT_VARIANTS = ['info', 'warning', 'critical', 'maintenance'];
export const ANNOUNCEMENT_TARGET_MODES = ['all', 'selected'];

export const ANNOUNCEMENT_VARIANT_LABELS = Object.freeze({
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
  maintenance: 'Maintenance',
});

/**
 * Map announcement variants onto the existing promo banner palette so the
 * visual feel is identical regardless of which banner is showing. We keep
 * a separate map (rather than re-using the promo names directly) so that
 * announcement styling can diverge later without breaking promo defaults.
 */
export const ANNOUNCEMENT_VARIANT_STYLES = Object.freeze({
  info: PROMO_VARIANT_STYLES.teal,
  warning: PROMO_VARIANT_STYLES.amber,
  critical: PROMO_VARIANT_STYLES.amber,
  maintenance: PROMO_VARIANT_STYLES.slate,
});

export const DEFAULT_ANNOUNCEMENT = Object.freeze({
  enabled: false,
  // Short all-caps prefix shown before the message (the RB UX "WHAT'S NEW"
  // eyebrow). Optional; empty hides it. See handoffs/01-design-pass.md §5.
  eyebrow: '',
  message: '',
  variant: 'info',
  ctaLabel: '',
  ctaUrl: '',
  dismissible: true,
  priority: 50,
  startsAt: null,
  endsAt: null,
  version: 1,
  target: {
    mode: 'all',
    uids: [],
    segments: {
      roles: [],
      planTiers: [],
      subscriptionStatuses: [],
      hasFarms: null,
    },
  },
});

function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

function asNumberOr(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function tsToIso(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value?.toDate === 'function') {
    try { return value.toDate().toISOString(); } catch { return null; }
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  return null;
}

/**
 * Coerce an announcement Firestore document into a safe shape the UI can
 * render. Always returns an object — even if the doc is missing or
 * malformed — so consumers can stay dumb and just check `enabled`.
 */
export function resolveAnnouncement(id, data) {
  const def = DEFAULT_ANNOUNCEMENT;
  if (data == null || typeof data !== 'object') {
    return { id, ...def, target: { ...def.target, segments: { ...def.target.segments } } };
  }
  const variant = ANNOUNCEMENT_VARIANTS.includes(data.variant) ? data.variant : def.variant;
  const target = data.target && typeof data.target === 'object' ? data.target : {};
  const mode = ANNOUNCEMENT_TARGET_MODES.includes(target.mode) ? target.mode : def.target.mode;
  const uids = Array.isArray(target.uids) ? target.uids.filter((u) => typeof u === 'string') : [];
  const segments = (target.segments && typeof target.segments === 'object') ? target.segments : {};

  return {
    id,
    enabled: data.enabled === true,
    eyebrow: isNonEmptyString(data.eyebrow) ? data.eyebrow.trim() : '',
    message: isNonEmptyString(data.message) ? data.message : def.message,
    variant,
    ctaLabel: isNonEmptyString(data.ctaLabel) ? data.ctaLabel : '',
    ctaUrl: isNonEmptyString(data.ctaUrl) ? data.ctaUrl : '',
    dismissible: data.dismissible !== false,
    priority: asNumberOr(data.priority, def.priority),
    startsAt: tsToIso(data.startsAt),
    endsAt: tsToIso(data.endsAt),
    version: Math.max(1, Math.floor(asNumberOr(data.version, def.version))),
    target: {
      mode,
      uids,
      segments: {
        roles: Array.isArray(segments.roles) ? segments.roles : [],
        planTiers: Array.isArray(segments.planTiers) ? segments.planTiers : [],
        subscriptionStatuses: Array.isArray(segments.subscriptionStatuses) ? segments.subscriptionStatuses : [],
        hasFarms: typeof segments.hasFarms === 'boolean' ? segments.hasFarms : null,
      },
    },
    createdBy: data.createdBy || null,
    createdAt: tsToIso(data.createdAt),
    updatedAt: tsToIso(data.updatedAt),
    updatedByEmail: data.updatedByEmail || null,
  };
}

/**
 * Returns true if the schedule window (startsAt / endsAt) currently makes
 * the announcement live. Either side is optional.
 */
export function isWithinScheduleWindow(announcement, now = Date.now()) {
  if (!announcement) return false;
  const start = announcement.startsAt ? Date.parse(announcement.startsAt) : null;
  const end = announcement.endsAt ? Date.parse(announcement.endsAt) : null;
  if (start && Number.isFinite(start) && now < start) return false;
  if (end && Number.isFinite(end) && now > end) return false;
  return true;
}

/** Build the localStorage key used to remember dismissals. */
export function dismissalKey(id) {
  return `${ANNOUNCEMENT_DISMISS_KEY_PREFIX}${id}`;
}
